import type { ArticleSection } from "@/content/projects";
import { draftQuestions } from "@/content/drafts";
import { PLACEHOLDER_PROSE, placeholderParagraphs } from "@/content/placeholder";

/**
 * Draft blocks are a development affordance only.
 *
 * IMPORTANT: the `process.env.NODE_ENV !== "production"` test is written inline
 * at its point of use, not hoisted into a `const SHOW_DRAFTS`. Bun substitutes
 * the literal and folds the comparison, but it does NOT propagate the result
 * through a const binding — with the hoisted form the branch survives, the
 * import is kept, and every draft question ends up as a readable string in the
 * production bundle. That regression shipped once; `site.test.ts` now greps the
 * built output for these questions so it cannot happen again quietly.
 */
function DraftBlock({ questions, heading }: { questions: string[]; heading: string }) {
  return (
    <div className="border border-dashed border-signal-300 bg-signal-100 p-5 flex flex-col gap-3">
      <span className="font-label text-label uppercase text-signal-700">Draft — not published · {heading}</span>
      <p className="text-body-sm text-text-body">
        Awaiting Mafazi's answers. This block is stripped from production builds; the section stays hidden until
        it has real content.
      </p>
      <ul className="m-0 pl-[1.1em] list-disc marker:text-signal-300 flex flex-col gap-2 text-body-sm text-text-primary">
        {questions.map((q) => (
          <li key={q}>{q}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The long-form body of a case study.
 *
 * Sections carry a mono rule-and-label header rather than a serif subhead: the
 * design system reserves the display face for headlines and project titles.
 */
export function ProjectArticle({ slug, sections }: { slug: string; sections: ArticleSection[] }) {
  const isProduction = process.env.NODE_ENV === "production";
  const drafts = isProduction ? {} : (draftQuestions[slug] ?? {});

  /**
   * One rule per section, in order:
   *   written copy            → render it
   *   nothing, in production  → lorem ipsum, while PLACEHOLDER_PROSE is on
   *   nothing, in development → the draft questions
   *   nothing at all          → drop the section
   */
  const rendered = sections
    .map((section) => {
      const written = section.paragraphs ?? [];
      const questions = drafts[section.heading] ?? [];
      const body =
        written.length > 0
          ? written
          : isProduction && PLACEHOLDER_PROSE
            ? placeholderParagraphs(section.heading)
            : [];
      return { heading: section.heading, body, questions };
    })
    .filter((section) => section.body.length > 0 || section.questions.length > 0);

  if (rendered.length === 0) return null;

  return (
    <article className="mt-9 max-w-measure flex flex-col gap-8">
      {rendered.map((section) => (
        <section key={section.heading} className="flex flex-col gap-4">
          <header className="border-t border-border-hairline pt-4">
            <h2 className="font-label text-label uppercase text-text-muted">{section.heading}</h2>
          </header>

          {section.body.map((paragraph) => (
            <p key={paragraph} className="text-body-lg text-text-body">
              {paragraph}
            </p>
          ))}

          {section.questions.length > 0 && (
            <DraftBlock questions={section.questions} heading={section.heading} />
          )}
        </section>
      ))}
    </article>
  );
}
